import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { STARTUP_API_URL } from "../apiConfig";
import { auth } from "../firebase";
import Navbar from "../components/navbar";
import Footer from "../components/Footer";
import RippleWaveLoader from "../components/ui/RippleWaveLoader";
import {
  FiArrowLeft, FiExternalLink, FiGlobe, FiDollarSign,
  FiCheckCircle, FiUsers, FiCalendar, FiMapPin,
  FiLinkedin, FiTwitter, FiMail, FiPhone,
  FiBriefcase, FiUser, FiTrendingUp, FiLayers,
  FiSearch, FiRefreshCw, FiAlertCircle, FiGrid
} from "react-icons/fi";

interface FundingRound {
  id: number;
  round_type: string;
  amount: number | null;
  date: string | null;
  investors: string | null;
  lead_investor: string | null;
}

interface Founder {
  id: number;
  name: string;
  title: string | null;
  linkedin_url: string | null;
}

interface StartupFull {
  id: number;
  name: string;
  description: string | null;
  short_description: string | null;
  website: string | null;
  logo_url: string | null;
  founded_year: number | null;
  employee_count: string | null;
  status: string | null;
  headquarters: string | null;
  funding_stage: string | null;
  total_funding: number | null;
  last_funding_amount: number | null;
  last_funding_date: string | null;
  valuation: number | null;
  tags: string[];
  data_source: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  crunchbase_url: string | null;
  tracxn_id: string | null;
  customer_engagements: number | null;
  has_deployment_record: boolean;
  is_verified: boolean;
  created_at: string | null;
  category: { id: number; name: string } | null;
  region: { country: string; state: string; city: string } | null;
  incubator: { id: number; name: string } | null;
  funding_rounds: FundingRound[];
  founders: Founder[];
  // live-only fields
  source_urls?: string[];
  recent_funding?: string;
  investors?: string[];
  confidence_score?: number;
  website_content?: string;
}

interface HunterContact {
  email: string;
  type: string | null;
  first_name: string | null;
  last_name: string | null;
  position: string | null;
  department: string | null;
  linkedin: string | null;
  confidence: number | null;
  phone: string | null;
}

interface ContactData {
  domain: string;
  company: string;
  total_emails: number;
  pattern: string | null;
  emails: HunterContact[];
}

const fmt = (amount: number | null) => {
  if (!amount) return "Undisclosed";
  if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
  if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
  if (amount >= 1e3) return `$${(amount / 1e3).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
};

const stageLabel = (s: string) =>
  ({
    pre_seed: "Pre-Seed", seed: "Seed", series_a: "Series A",
    series_b: "Series B", series_c: "Series C", series_d_plus: "Series D+",
    ipo: "IPO", acquired: "Acquired", unknown: "Unknown",
  }[s] || s);

const StartupDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [startup, setStartup] = useState<StartupFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add to DB state
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "duplicate" | "error">("idle");

  // Persist saved state across navigation via localStorage
  useEffect(() => {
    if (!startup?.name) return;
    const key = startup.name.toLowerCase().trim();
    const saved: string[] = JSON.parse(localStorage.getItem("ucdp_saved_startups") || "[]");
    if (saved.includes(key)) setSaveStatus("saved");
  }, [startup?.name]);

  // Hunter.io contact enrichment
  const [contacts, setContacts] = useState<ContactData | null>(null);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);

  // Deep enrichment for live startups
  interface EnrichedData {
    website?: string | null;
    founded_year?: number | null;
    funding_stage?: string | null;
    total_funding?: number | null;
    valuation?: number | null;
    recent_funding?: string | null;
    funding_rounds?: { round: string; amount: number | null; date: string | null; investors: string[] }[];
    founders?: { name: string; title: string | null; linkedin_url: string | null; background?: string | null }[];
    investors?: string[];
    solutions?: string[];
    customers?: string[];
    clients?: string[];
    technology_stack?: string[];
    description?: string | null;
    company_overview?: string | null;
    employee_count?: string | null;
    headquarters?: string | null;
    phone?: string | null;
    mobile?: string | null;
    additional_phones?: string[];
    email?: string | null;
    additional_emails?: string[];
    articles?: { title: string; url?: string | null; date?: string | null; source?: string | null }[];
    case_studies?: { title: string; url?: string | null; client?: string | null; outcome?: string | null }[];
    ip_published?: { title: string; number?: string | null; url?: string | null }[] | string[];
    ip_applied?: { title: string; number?: string | null; url?: string | null }[] | string[];
  }
  const [enriched, setEnriched] = useState<EnrichedData | null>(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichElapsed, setEnrichElapsed] = useState(0);

  // RAG Chatbot
  interface ChatMessage { role: "user" | "assistant"; content: string; }
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatReady, setChatReady] = useState(false);
  const [chatIndexing, setChatIndexing] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const chatSectionRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      if (!chatSectionRef.current) return;
      const rect = chatSectionRef.current.getBoundingClientRect();
      // Hide when chat section is visible, show again when scrolled back up above it
      setShowScrollHint(rect.top > window.innerHeight);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const state = (location.state as any)?.startup;
    if (state && id === "live") {
      const rawFounders = state.founders || [];
      const founders = rawFounders.map((f: any) =>
        typeof f === "string"
          ? { id: Math.random(), name: f, title: null, linkedin_url: null }
          : f
      );
      const s = {
        ...state,
        description: state.description || state.short_description || null,
        funding_rounds: state.funding_rounds || [],
        founders,
        tags: Array.isArray(state.tags) ? state.tags : (state.tags ? String(state.tags).split(",") : []),
      };
      setStartup(s);
      setLoading(false);
      // Auto-trigger deep enrichment for live startups
      fetchEnrichment(state.name, state.website, state.tracxn_id, state.crunchbase_url);
      return;
    }
    if (id && id !== "live") fetchDetail(parseInt(id));
  }, [id]);

  const applyEnriched = (d: EnrichedData) => {
    setEnriched(d);
    setStartup(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        website: prev.website || d.website || null,
        founded_year: prev.founded_year || d.founded_year || null,
        funding_stage: (prev.funding_stage && prev.funding_stage !== "unknown") ? prev.funding_stage : (d.funding_stage || prev.funding_stage),
        total_funding: prev.total_funding || d.total_funding || null,
        headquarters: prev.headquarters || d.headquarters || null,
        description: prev.description || d.description || null,
        founders: prev.founders?.length ? prev.founders : (d.founders?.map(f => ({
          id: Math.random(), name: f.name, title: f.title || null, linkedin_url: f.linkedin_url || null
        })) || []),
      };
    });
  };

  const fetchEnrichment = async (
    name: string,
    website?: string | null,
    tracxn_id?: string | null,
    crunchbase_url?: string | null,
  ) => {
    if (!name) return;

    // v7 = fixed email filter (info@/contact@/admin@ now allowed); old cache auto-invalidated
    const cacheKey = `enriched_v8_${name.toLowerCase().replace(/\s+/g, "_")}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        applyEnriched(JSON.parse(cached) as EnrichedData);
        return;
      }
    } catch {}

    try {
      setEnrichLoading(true);
      setEnrichElapsed(0);
      const res = await axios.post(`${STARTUP_API_URL}/api/v1/startups/enrich`, {
        name,
        website: website || null,
        tracxn_id: tracxn_id || null,
        crunchbase_url: crunchbase_url || null,
      }, { timeout: 120000 });
      if (res.data.status === "success" && res.data.data) {
        const d = res.data.data as EnrichedData;
        try { sessionStorage.setItem(cacheKey, JSON.stringify(d)); } catch {}
        applyEnriched(d);
      }
    } catch { /* silent — enrichment is best-effort */ }
    finally { setEnrichLoading(false); }
  };

  const fetchDetail = async (sid: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${STARTUP_API_URL}/api/v1/startups/detail/${sid}`);
      if (res.data.status === "success") {
        const s = res.data.data;
        setStartup(s);
        // Trigger enrichment for all DB startups to unlock clients, articles, IP etc.
        if (s?.name) {
          fetchEnrichment(s.name, s.website, s.tracxn_id, s.crunchbase_url);
        }
      } else setError("Startup not found");
    } catch (err: any) {
      setError(err.response?.status === 404 ? "Startup not found" : err.code === "ERR_NETWORK" ? "Unable to connect to server" : "Failed to load startup details");
    } finally {
      setLoading(false);
    }
  };

  // Poll chat-ready status once startup loads
  useEffect(() => {
    if (!startup?.website) return;
    const checkReady = async () => {
      try {
        const res = await axios.get(`${STARTUP_API_URL}/api/v1/startups/chat/status`, { params: { website: startup.website } });
        if (res.data.ready) {
          setChatReady(true);
        }
      } catch {}
    };
    checkReady();
    // Poll every 5s until ready
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${STARTUP_API_URL}/api/v1/startups/chat/status`, { params: { website: startup.website } });
        if (res.data.ready) { setChatReady(true); clearInterval(interval); }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [startup?.website]);

  // Scroll chat to bottom on new message
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatLoading]);

  // Elapsed-time counter shown while enrichment is running
  useEffect(() => {
    if (!enrichLoading) { setEnrichElapsed(0); return; }
    const t = setInterval(() => setEnrichElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [enrichLoading]);

  const sendChatMessage = async () => {
    const q = chatInput.trim();
    if (!q || chatLoading || !startup?.website) return;
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: q }]);
    setChatLoading(true);
    if (!chatReady) setChatIndexing(true);
    try {
      const res = await axios.post(`${STARTUP_API_URL}/api/v1/startups/chat`, {
        website: startup.website,
        startup_name: startup.name,
        question: q,
      }, { timeout: 120000 });
      setChatMessages(prev => [...prev, { role: "assistant", content: res.data.answer || "No answer returned." }]);
      setChatReady(true);
    } catch {
      setChatMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setChatLoading(false);
      setChatIndexing(false);
    }
  };

  const fetchContacts = async (website: string) => {
    try {
      setContactsLoading(true);
      setContactsError(null);
      const res = await axios.get(`${STARTUP_API_URL}/api/v1/startups/contacts`, {
        params: { domain: website },
        timeout: 20000,
      });
      if (res.data.status === "success") {
        setContacts(res.data.data);
        if (res.data.data.emails.length === 0) {
          setContactsError("No verified contacts found for this domain.");
        }
      } else {
        setContactsError(res.data.message || "Failed to fetch contacts");
      }
    } catch (err: any) {
      if (err.response?.status === 501) {
        setContactsError("Hunter.io API key not configured. Add HUNTER_API_KEY to .env");
      } else {
        setContactsError(err.response?.data?.message || "Failed to fetch contacts");
      }
    } finally {
      setContactsLoading(false);
    }
  };

  const _persistSaved = (name: string) => {
    const key = name.toLowerCase().trim();
    const saved: string[] = JSON.parse(localStorage.getItem("ucdp_saved_startups") || "[]");
    if (!saved.includes(key)) {
      saved.push(key);
      localStorage.setItem("ucdp_saved_startups", JSON.stringify(saved));
    }
  };

  const handleAddToDb = async () => {
    if (!startup || saving) return;
    setSaving(true);
    setSaveStatus("idle");
    try {
      // Prefer a fresh Firebase token (auto-refreshes on expiry) over the cached one
      const token = await auth.currentUser?.getIdToken() ?? localStorage.getItem("authToken");

      // Extract domain keywords from the originating search query and merge into tags
      // so future DB searches for the same query will find this startup.
      const _stopWords = new Set(["find","show","companies","startups","solutions","in","for",
        "with","and","or","the","a","an","can","you","please","india","indian","based",
        "looking","want","need","give","tell","top","best","leading","new","latest","list","related"]);
      const searchQuery: string = (location.state as any)?.search_query || "";
      const queryKeywords = searchQuery.length > 0
        ? [...new Set(searchQuery.toLowerCase().split(/\W+/).filter(w => w.length > 2 && !_stopWords.has(w)))]
        : [];
      const existingTags: string[] = Array.isArray(startup.tags) ? startup.tags.map(t => t.toLowerCase()) : [];
      const mergedTags = [...existingTags, ...queryKeywords.filter(k => !existingTags.includes(k))];

      const payload = {
        name: startup.name,
        short_description: startup.short_description || null,
        description: startup.description || null,
        website: startup.website || null,
        founded_year: startup.founded_year ?? null,
        employee_count: startup.employee_count || null,
        headquarters: startup.headquarters || null,
        funding_stage: startup.funding_stage || "unknown",
        total_funding: startup.total_funding ?? null,
        email: startup.email || null,
        phone: startup.phone || null,
        linkedin_url: startup.linkedin_url || null,
        twitter_url: startup.twitter_url || null,
        crunchbase_url: startup.crunchbase_url || null,
        tags: mergedTags.length > 0 ? mergedTags.join(",") : null,
        data_source: "live",
      };
      await axios.post(`${STARTUP_API_URL}/api/v1/admin/startups`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Patch cached search results so the card shows "Database" badge on back navigation
      try {
        const cached = sessionStorage.getItem("startupSearchState");
        if (cached && startup) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed.startups)) {
            parsed.startups = parsed.startups.map((s: any) =>
              s.name?.toLowerCase() === startup.name?.toLowerCase()
                ? { ...s, data_source: "database" }
                : s
            );
            sessionStorage.setItem("startupSearchState", JSON.stringify(parsed));
          }
        }
      } catch {}
      setSaveStatus("saved");
      _persistSaved(startup.name);
    } catch (err: any) {
      console.error("[AddToDb]", err?.response?.status, err?.response?.data);
      const msg: string = err?.response?.data?.message || "";
      if (err?.response?.status === 400 && msg.toLowerCase().includes("already exists")) {
        setSaveStatus("duplicate");
        _persistSaved(startup.name);
      } else {
        setSaveStatus("error");
      }
    } finally {
      setSaving(false);
    }
  };

  const loc = () => {
    if (startup?.region) return [startup.region.city, startup.region.state, startup.region.country].filter(Boolean).join(", ");
    return startup?.headquarters || null;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: "radial-gradient(35% 19% at 50% 0%, rgba(255,84,31,0.5) 0%, rgba(0,0,0,0) 100%), black",
        color: "white",
      }}
    >
      <div className="w-full bg-transparent border-b border-white/10">
        <Navbar onSearchClick={() => {}} />
      </div>

      {/* Scroll hint — fixed right side top, toggles based on chat section visibility */}
      {startup && (
        <div className="fixed right-5 top-28 z-40 flex flex-col items-center gap-2 pointer-events-none select-none"
          style={{ opacity: showScrollHint ? 1 : 0, transition: "opacity 0.35s ease" }}>
          <div style={{
            background: "rgba(0,0,0,0.6)", backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,84,31,0.3)", borderRadius: 999,
            padding: "6px 14px",
          }}>
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,84,31,0.9)", whiteSpace: "nowrap" }}>
              Ask AI about this startup ↓
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            {[0, 1, 2].map(i => (
              <svg key={i} width="14" height="8" viewBox="0 0 14 8" fill="none"
                style={{ animation: `scrollArrow 1.6s ${i * 0.22}s ease-in-out infinite`, opacity: 0 }}>
                <path d="M1 1l6 6 6-6" stroke="#FF541F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ))}
          </div>
          <style>{`
            @keyframes scrollArrow {
              0%   { opacity: 0; transform: translateY(-5px); }
              40%  { opacity: 0.9; transform: translateY(0); }
              80%  { opacity: 0.2; transform: translateY(5px); }
              100% { opacity: 0; transform: translateY(5px); }
            }
          `}</style>
        </div>
      )}

      <div className="flex-1 w-full max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition mb-8 group"
        >
          <FiArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Back to Startup Search
        </button>

        {loading ? (
          <div className="space-y-6 animate-pulse">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-white/10" />
              <div className="space-y-3 flex-1">
                <div className="h-8 bg-white/10 rounded w-64" />
                <div className="h-4 bg-white/5 rounded w-40" />
              </div>
            </div>
            <div className="h-px bg-white/10" />
            <div className="space-y-3">
              <div className="h-4 bg-white/10 rounded w-full" />
              <div className="h-4 bg-white/10 rounded w-3/4" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white/5 rounded-xl" />)}
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <FiGlobe className="w-10 h-10 text-red-400" />
            </div>
            <p className="text-red-400 text-lg font-medium">{error}</p>
            <button onClick={() => navigate(-1)} className="mt-6 px-6 py-3 rounded-xl bg-[#FF541F] hover:bg-[#e54a1a] transition">
              Back to Directory
            </button>
          </div>
        ) : startup ? (
          <div className="space-y-8">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row items-start gap-6">
              {startup.logo_url ? (
                <img src={startup.logo_url} alt={startup.name} className="w-20 h-20 rounded-2xl object-cover border border-white/10" />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF541F] to-[#FF3C00] flex items-center justify-center text-white font-bold text-3xl flex-shrink-0">
                  {startup.name.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Sora" }}>{startup.name}</h1>
                  {startup.is_verified && (
                    <span className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 flex items-center gap-1"><FiCheckCircle className="w-3 h-3" /> Verified</span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2 flex-wrap text-gray-400 text-sm">
                  {startup.category && <span className="flex items-center gap-1"><FiLayers className="w-4 h-4" /> {startup.category.name}</span>}
                  {loc() && <span className="flex items-center gap-1"><FiMapPin className="w-4 h-4" /> {loc()}</span>}
                  {startup.founded_year && <span className="flex items-center gap-1"><FiCalendar className="w-4 h-4" /> Est. {startup.founded_year}</span>}
                  {startup.employee_count && <span className="flex items-center gap-1"><FiUsers className="w-4 h-4" /> {startup.employee_count} employees</span>}
                </div>
                {/* Actions */}
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  {startup.website && (
                    <a href={startup.website} target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 rounded-xl bg-[#FF541F] hover:bg-[#e54a1a] transition font-semibold flex items-center gap-2 text-base shadow-lg" style={{ color: "#ffffff" }}>
                      <FiExternalLink className="w-4 h-4" /> Visit Website
                    </a>
                  )}
                  {startup.linkedin_url && (
                    <a href={startup.linkedin_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-xl border border-white/20 hover:bg-white/10 transition text-sm flex items-center gap-2">
                      <FiLinkedin className="w-4 h-4" /> LinkedIn
                    </a>
                  )}
                  {startup.twitter_url && (
                    <a href={startup.twitter_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-xl border border-white/20 hover:bg-white/10 transition text-sm flex items-center gap-2">
                      <FiTwitter className="w-4 h-4" /> Twitter
                    </a>
                  )}
                  {startup.crunchbase_url && (
                    <a href={startup.crunchbase_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-xl border border-white/20 hover:bg-white/10 transition text-sm flex items-center gap-2">
                      <FiTrendingUp className="w-4 h-4" /> Crunchbase
                    </a>
                  )}
                  {localStorage.getItem("userRole") === "admin" && (
                    <button
                      onClick={handleAddToDb}
                      disabled={saving || saveStatus === "saved" || saveStatus === "duplicate"}
                      className="ml-auto px-4 py-2 rounded-xl border transition text-sm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        borderColor: (saveStatus === "saved" || saveStatus === "duplicate") ? "rgba(34,197,94,0.5)" : saveStatus === "error" ? "rgba(239,68,68,0.5)" : "rgba(255,84,31,0.5)",
                        color: (saveStatus === "saved" || saveStatus === "duplicate") ? "#22c55e" : saveStatus === "error" ? "#ef4444" : "#FF541F",
                        background: (saveStatus === "saved" || saveStatus === "duplicate") ? "rgba(34,197,94,0.1)" : saveStatus === "error" ? "rgba(239,68,68,0.1)" : "transparent",
                      }}
                    >
                      <FiGrid className="w-4 h-4" />
                      {saving ? "Saving..." : (saveStatus === "saved" || saveStatus === "duplicate") ? "Saved" : saveStatus === "error" ? "Save Failed" : "Add to Startup List"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                <p className="text-gray-400 text-sm font-medium mb-1">Total Funding</p>
                <p className="text-green-400 text-2xl font-bold">
                  {fmt(startup.total_funding || enriched?.total_funding || null)}
                </p>
              </div>
              <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                <p className="text-gray-400 text-sm font-medium mb-1">Funding Stage</p>
                <p className="text-blue-400 text-2xl font-bold">
                  {stageLabel((startup.funding_stage && startup.funding_stage !== "unknown") ? startup.funding_stage : (enriched?.funding_stage || startup.funding_stage || ""))}
                </p>
              </div>
              <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                <p className="text-gray-400 text-sm font-medium mb-1">Valuation</p>
                <p className="text-orange-400 text-2xl font-bold">{(startup.valuation || enriched?.valuation) ? fmt(startup.valuation || enriched?.valuation || null) : "—"}</p>
              </div>
              <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                <p className="text-gray-400 text-sm font-medium mb-1">Founded</p>
                <p className="text-white text-2xl font-bold">{startup.founded_year || enriched?.founded_year || "—"}</p>
              </div>
            </div>

            {/* COMPANY QUICK-FACTS STRIP */}
            {(() => {
              const facts: { icon: React.ReactNode; label: string; value: string }[] = [];
              const empCount = startup.employee_count || enriched?.employee_count;
              if (empCount) facts.push({ icon: <FiUsers className="w-4 h-4 text-[#FF541F]" />, label: "Team Size", value: empCount });
              const hq = startup.headquarters || enriched?.headquarters || (startup.region ? [startup.region.city, startup.region.state, startup.region.country].filter(Boolean).join(", ") : null);
              if (hq) facts.push({ icon: <FiMapPin className="w-4 h-4 text-[#FF541F]" />, label: "Headquarters", value: hq });
              if (startup.category?.name) facts.push({ icon: <FiLayers className="w-4 h-4 text-[#FF541F]" />, label: "Sector", value: startup.category.name });
              if (startup.data_source) facts.push({ icon: <FiCheckCircle className="w-4 h-4 text-[#FF541F]" />, label: "Data Source", value: startup.data_source.charAt(0).toUpperCase() + startup.data_source.slice(1) });
              if (startup.last_funding_date) facts.push({ icon: <FiCalendar className="w-4 h-4 text-[#FF541F]" />, label: "Last Funding", value: new Date(startup.last_funding_date).toLocaleDateString("en-US", { year: "numeric", month: "short" }) });
              if (startup.last_funding_amount) facts.push({ icon: <FiDollarSign className="w-4 h-4 text-[#FF541F]" />, label: "Last Round", value: fmt(startup.last_funding_amount) });
              if (!facts.length) return null;
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                  {facts.map((f, i) => (
                    <div key={i} className="p-3 rounded-xl border border-white/10 bg-white/[0.03] flex flex-col gap-1">
                      <div className="flex items-center gap-1.5 text-gray-500 text-xs font-medium">
                        {f.icon} {f.label}
                      </div>
                      <p className="text-white text-sm font-semibold truncate" title={f.value}>{f.value}</p>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* ENRICHMENT LOADING */}
            {enrichLoading && (
              <div className="rounded-2xl border border-white/10 overflow-hidden"
                style={{ background: "linear-gradient(135deg, rgba(255,84,31,0.06) 0%, rgba(0,0,0,0) 100%)" }}>
                <div className="px-6 py-8 flex flex-col items-center gap-4 text-center">
                  <RippleWaveLoader
                    messages={[
                      "Scanning Tracxn & Crunchbase for funding data…",
                      "Fetching founders & leadership profiles…",
                      "Extracting clients & customer case studies…",
                      "Analysing technology stack & solutions…",
                      "Searching recent news & press releases…",
                      "Mining patent filings & IP records…",
                      "Fetching contact details from company website…",
                      "Almost there — compiling all insights…",
                    ]}
                  />
                  <div className="flex flex-col items-center gap-1 mt-1">
                    <p className="text-sm font-semibold text-white/80">
                      Deep enrichment in progress
                    </p>
                    <p className="text-xs text-gray-500">
                      This typically takes <span className="text-[#FF541F] font-bold">30–60 seconds</span> — hang tight
                    </p>
                  </div>
                  {/* Elapsed timer */}
                  <div className="flex items-center gap-2 px-4 py-1.5 rounded-full"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF541F] animate-pulse inline-block" />
                    <span className="text-xs text-gray-400 font-medium tabular-nums">
                      {enrichElapsed < 60
                        ? `${enrichElapsed}s elapsed`
                        : `${Math.floor(enrichElapsed / 60)}m ${enrichElapsed % 60}s elapsed`}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ABOUT */}
            {(startup.description || startup.short_description || enriched?.description || enriched?.company_overview) && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiBriefcase className="w-5 h-5 text-[#FF541F]" /> About
                </h2>
                {(() => {
                  // Pick the single longest/richest description — avoids all repetition
                  const candidates = [
                    enriched?.description,
                    enriched?.company_overview,
                    startup.description,
                    startup.short_description,
                  ].filter((s): s is string => !!s && s.trim().length > 0);
                  const best = candidates.reduce((a, b) => a.length >= b.length ? a : b, "");
                  return <p className="text-gray-300 leading-relaxed whitespace-pre-line">{best}</p>;
                })()}
                {/* Phone / Mobile */}
                {(enriched?.phone || enriched?.mobile || startup.phone) && (
                  <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/10">
                    {(enriched?.phone || startup.phone) && (
                      <a href={`tel:${enriched?.phone || startup.phone}`} className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#FF541F] transition">
                        <FiPhone className="w-4 h-4 text-[#FF541F]" /> {enriched?.phone || startup.phone}
                      </a>
                    )}
                    {enriched?.mobile && enriched.mobile !== enriched?.phone && (
                      <a href={`tel:${enriched.mobile}`} className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#FF541F] transition">
                        <FiPhone className="w-4 h-4 text-green-400" /> {enriched.mobile} <span className="text-xs text-gray-500">(mobile)</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAGS / KEYWORDS */}
            {startup.tags && startup.tags.length > 0 && (
              <div className="p-5 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FiLayers className="w-4 h-4 text-[#FF541F]" /> Keywords & Tags
                </h2>
                <div className="flex flex-wrap gap-2">
                  {startup.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-gray-400 hover:border-[#FF541F]/40 hover:text-gray-300 transition">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* RECENT FUNDING (live results) */}
            {(startup.recent_funding || enriched?.recent_funding) && (
              <div className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5">
                <p className="text-blue-400 text-sm font-medium flex items-center gap-2">
                  <FiDollarSign className="w-4 h-4" /> {startup.recent_funding || enriched?.recent_funding}
                </p>
              </div>
            )}


            {/* SOLUTIONS */}
            {enriched?.solutions && enriched.solutions.length > 0 && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiLayers className="w-5 h-5 text-[#FF541F]" /> Solutions & Products
                </h2>
                <div className="flex flex-wrap gap-2">
                  {enriched.solutions.map((s, i) => (
                    <span key={i} className="px-3 py-2 rounded-xl text-sm bg-orange-500/10 border border-orange-500/20 text-orange-300">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* CLIENTS */}
            {(() => {
              const caseStudyClients = (enriched?.case_studies || [])
                .map(cs => cs.client).filter(Boolean) as string[];
              const allClients = [...new Set([
                ...(enriched?.clients || []),
                ...(enriched?.customers || []),
                ...caseStudyClients,
              ])].filter(c => c.trim().length > 1);

              // Always show the section — with data, loading state, or empty state
              return (
                <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                    <FiUsers className="w-5 h-5 text-[#FF541F]" /> Clients & Customers
                    {allClients.length > 0 && (
                      <span className="text-xs text-gray-500 font-normal ml-1">({allClients.length})</span>
                    )}
                  </h2>
                  {enrichLoading ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <FiRefreshCw className="w-4 h-4 animate-spin text-[#FF541F]" />
                      Fetching client data...
                    </div>
                  ) : allClients.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {allClients.map((c, i) => (
                        <span key={i} className="px-3 py-2 rounded-xl text-sm bg-purple-500/10 border border-purple-500/20 text-purple-300">{c}</span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm flex items-center gap-2">
                      <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                      No client data found in public sources for this company.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* ARTICLES & CASE STUDIES */}
            {((enriched?.articles && enriched.articles.length > 0) || (enriched?.case_studies && enriched.case_studies.length > 0)) && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiSearch className="w-5 h-5 text-[#FF541F]" /> Articles & Case Studies
                </h2>
                <div className="space-y-3">
                  {enriched?.articles?.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-500/20 text-blue-400 shrink-0 mt-0.5">Article</span>
                      <div className="min-w-0">
                        {a.url ? (
                          <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-[#FF541F] transition font-medium">{a.title}</a>
                        ) : (
                          <p className="text-sm text-white font-medium">{a.title}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {a.source && <span>{a.source}</span>}
                          {a.date && <span>{a.date}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                  {enriched?.case_studies?.map((cs, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-green-500/20 text-green-400 shrink-0 mt-0.5">Case Study</span>
                      <div className="min-w-0">
                        {cs.url ? (
                          <a href={cs.url} target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-[#FF541F] transition font-medium">{cs.title}</a>
                        ) : (
                          <p className="text-sm text-white font-medium">{cs.title}</p>
                        )}
                        {cs.client && <p className="text-xs text-gray-400 mt-1">Client: {cs.client}</p>}
                        {cs.outcome && <p className="text-xs text-gray-500 mt-0.5">{cs.outcome}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* IP & PATENTS */}
            {((enriched?.ip_published && enriched.ip_published.length > 0) || (enriched?.ip_applied && enriched.ip_applied.length > 0)) && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiCheckCircle className="w-5 h-5 text-[#FF541F]" /> IP & Patents
                </h2>
                <div className="space-y-4">
                  {enriched?.ip_published && enriched.ip_published.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <FiCheckCircle className="w-3 h-3" /> Published / Granted ({enriched.ip_published.length})
                      </p>
                      <div className="space-y-2">
                        {enriched.ip_published.map((ip, i) => {
                          const isObj = typeof ip === "object" && ip !== null;
                          const title = isObj ? (ip as any).title : ip as string;
                          const number = isObj ? (ip as any).number : null;
                          const url = isObj ? (ip as any).url : null;
                          return (
                            <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/5">
                              <span className="text-green-500 mt-1 shrink-0">•</span>
                              <div className="min-w-0">
                                {url ? (
                                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-[#FF541F] transition">{title}</a>
                                ) : (
                                  <span className="text-sm text-gray-300">{title}</span>
                                )}
                                {number && <span className="text-xs text-green-400 ml-2 font-mono">{number}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {enriched?.ip_applied && enriched.ip_applied.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <FiAlertCircle className="w-3 h-3" /> Applied / Pending ({enriched.ip_applied.length})
                      </p>
                      <div className="space-y-2">
                        {enriched.ip_applied.map((ip, i) => {
                          const isObj = typeof ip === "object" && ip !== null;
                          const title = isObj ? (ip as any).title : ip as string;
                          const number = isObj ? (ip as any).number : null;
                          const url = isObj ? (ip as any).url : null;
                          return (
                            <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/5">
                              <span className="text-yellow-500 mt-1 shrink-0">•</span>
                              <div className="min-w-0">
                                {url ? (
                                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-[#FF541F] transition">{title}</a>
                                ) : (
                                  <span className="text-sm text-gray-300">{title}</span>
                                )}
                                {number && <span className="text-xs text-yellow-400 ml-2 font-mono">{number}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TECHNOLOGY STACK (enriched) */}
            {enriched?.technology_stack && enriched.technology_stack.length > 0 && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiGrid className="w-5 h-5 text-[#FF541F]" /> Technology Stack
                </h2>
                <div className="flex flex-wrap gap-2">
                  {enriched.technology_stack.map((t, i) => (
                    <span key={i} className="px-3 py-2 rounded-xl text-sm bg-sky-500/10 border border-sky-500/20 text-sky-300">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* ENRICHED FUNDING ROUNDS — shown when DB rounds missing */}
            {(!startup.funding_rounds?.length && enriched?.funding_rounds && enriched.funding_rounds.length > 0) && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiDollarSign className="w-5 h-5 text-[#FF541F]" /> Funding Rounds
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-gray-500 text-left">
                        <th className="pb-3 pr-4 font-medium">Round</th>
                        <th className="pb-3 pr-4 font-medium">Amount</th>
                        <th className="pb-3 pr-4 font-medium">Date</th>
                        <th className="pb-3 font-medium">Investors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enriched.funding_rounds.map((r, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="py-3 pr-4"><span className="px-2.5 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">{r.round || "Unknown"}</span></td>
                          <td className="py-3 pr-4 text-green-400 font-medium">{r.amount ? fmt(r.amount) : "Undisclosed"}</td>
                          <td className="py-3 pr-4 text-gray-400">{r.date || "—"}</td>
                          <td className="py-3 text-gray-400">{r.investors?.join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* FUNDING ROUNDS (from DB) */}
            {startup.funding_rounds && startup.funding_rounds.length > 0 && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiDollarSign className="w-5 h-5 text-[#FF541F]" /> Funding Rounds
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-gray-500 text-left">
                        <th className="pb-3 pr-4 font-medium">Round</th>
                        <th className="pb-3 pr-4 font-medium">Amount</th>
                        <th className="pb-3 pr-4 font-medium">Date</th>
                        <th className="pb-3 pr-4 font-medium">Lead Investor</th>
                        <th className="pb-3 font-medium">Investors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {startup.funding_rounds.map((r) => (
                        <tr key={r.id} className="border-b border-white/5">
                          <td className="py-3 pr-4"><span className="px-2.5 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">{r.round_type || "Unknown"}</span></td>
                          <td className="py-3 pr-4 text-green-400 font-medium">{fmt(r.amount)}</td>
                          <td className="py-3 pr-4 text-gray-400">{r.date ? new Date(r.date).toLocaleDateString("en-US", { year: "numeric", month: "short" }) : "—"}</td>
                          <td className="py-3 pr-4 text-white">{r.lead_investor || "—"}</td>
                          <td className="py-3 text-gray-400 max-w-xs truncate">{r.investors || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* INVESTORS (live results + enriched) */}
            {((startup.investors && startup.investors.length > 0) || (enriched?.investors && enriched.investors.length > 0)) && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                  <FiTrendingUp className="w-5 h-5 text-[#FF541F]" /> Investors
                </h2>
                <div className="flex flex-wrap gap-2">
                  {[...new Set([...(startup.investors || []), ...(enriched?.investors || [])])].map((inv, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full text-sm bg-green-500/10 border border-green-500/20 text-green-400">{inv}</span>
                  ))}
                </div>
              </div>
            )}

            {/* FOUNDERS */}
            {(() => {
              // Merge DB founders with enriched backgrounds
              const enrichedMap = new Map(
                (enriched?.founders || []).map(f => [f.name.toLowerCase(), f])
              );
              const displayFounders = (startup.founders?.length
                ? startup.founders.map(f => ({
                    ...f,
                    background: enrichedMap.get(f.name.toLowerCase())?.background || null,
                    linkedin_url: f.linkedin_url || enrichedMap.get(f.name.toLowerCase())?.linkedin_url || null,
                  }))
                : (enriched?.founders?.map((f, idx) => ({
                    id: idx,
                    name: f.name,
                    title: f.title || null,
                    linkedin_url: f.linkedin_url || null,
                    background: f.background || null,
                  })) || [])
              );
              return displayFounders.length > 0 ? (
                <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                    <FiUser className="w-5 h-5 text-[#FF541F]" /> Founders
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {displayFounders.map((f) => (
                      <div key={f.id} className="p-4 rounded-xl border border-white/10 bg-white/[0.03] flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF541F] to-[#FF3C00] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">{f.name.charAt(0)}</div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-white">{f.name}</p>
                          {f.title && <p className="text-[#FF541F] text-sm mt-0.5">{f.title}</p>}
                          {(f as any).background && (
                            <p className="text-gray-400 text-xs mt-2 leading-relaxed">{(f as any).background}</p>
                          )}
                          {f.linkedin_url && (
                            <a href={f.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 mt-2">
                              <FiLinkedin className="w-3 h-3" /> LinkedIn Profile
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* CONTACT & DETAILS — always shown */}
            <div className="p-6 rounded-xl border border-white/10 bg-white/5">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                <FiMail className="w-5 h-5 text-[#FF541F]" /> Contact & Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {/* Email */}
                {(startup.email || enriched?.email) && (
                  <div className="flex items-center gap-3">
                    <FiMail className="w-4 h-4 text-[#FF541F] flex-shrink-0" />
                    <a href={`mailto:${enriched?.email || startup.email}`} className="text-blue-400 hover:text-blue-300 truncate font-medium">
                      {enriched?.email || startup.email}
                    </a>
                  </div>
                )}
                {enriched?.additional_emails?.map((em, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <FiMail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <a href={`mailto:${em}`} className="text-blue-400 hover:text-blue-300 truncate">{em}</a>
                  </div>
                ))}
                {/* Primary phone */}
                {(startup.phone || enriched?.phone) && (
                  <div className="flex items-center gap-3">
                    <FiPhone className="w-4 h-4 text-[#FF541F] flex-shrink-0" />
                    <a href={`tel:${enriched?.phone || startup.phone}`} className="text-green-400 hover:text-green-300 font-medium">
                      {enriched?.phone || startup.phone}
                    </a>
                  </div>
                )}
                {/* Mobile / second number */}
                {enriched?.mobile && enriched.mobile !== enriched?.phone && (
                  <div className="flex items-center gap-3">
                    <FiPhone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <a href={`tel:${enriched.mobile}`} className="text-green-400 hover:text-green-300">
                      {enriched.mobile}
                    </a>
                  </div>
                )}
                {/* Additional regional numbers (e.g. US, EU, Asia) */}
                {enriched?.additional_phones?.map((ph, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <FiPhone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <a href={`tel:${ph}`} className="text-green-400 hover:text-green-300">{ph}</a>
                  </div>
                ))}
                {/* Incubator / source */}
                {startup.incubator && (
                  <div className="flex items-center gap-3">
                    <FiBriefcase className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-300">Incubator: {startup.incubator.name}</span>
                  </div>
                )}
                {startup.data_source && (
                  <div className="flex items-center gap-3">
                    <FiLayers className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-300 capitalize">Source: {startup.data_source}</span>
                  </div>
                )}
                {/* Empty state */}
                {!startup.email && !enriched?.email && !startup.phone && !enriched?.phone && !enrichLoading && (
                  <p className="text-gray-500 text-xs col-span-2 flex items-center gap-2">
                    <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                    No contact info found on public sources.
                  </p>
                )}
                {enrichLoading && !startup.phone && !enriched?.phone && (
                  <p className="text-gray-500 text-xs col-span-2 flex items-center gap-2">
                    <FiRefreshCw className="w-3 h-3 animate-spin text-[#FF541F]" />
                    Extracting contact details from website...
                  </p>
                )}
              </div>
            </div>

            {/* FIND CONTACTS (Hunter.io) */}
            {startup.website && (
              <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: "Sora" }}>
                    <FiSearch className="w-5 h-5 text-[#FF541F]" /> Verified Contacts
                  </h2>
                  {!contacts && !contactsLoading && (
                    <button
                      onClick={() => fetchContacts(startup.website!)}
                      className="px-4 py-2 rounded-xl bg-[#FF541F] hover:bg-[#e54a1a] transition text-sm font-semibold flex items-center gap-2"
                    >
                      <FiSearch className="w-4 h-4" /> Find Contacts
                    </button>
                  )}
                </div>

                {contactsLoading && (
                  <div className="flex items-center gap-3 text-gray-400 py-4">
                    <FiRefreshCw className="w-5 h-5 animate-spin text-[#FF541F]" />
                    <span className="text-sm">Looking up contacts via Hunter.io...</span>
                  </div>
                )}

                {contactsError && !contacts?.emails.length && (
                  <div className="flex items-center gap-2 text-yellow-400 text-sm py-2">
                    <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                    {contactsError}
                  </div>
                )}

                {contacts && contacts.emails.length > 0 && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {contacts.emails.map((c, i) => (
                        <div key={i} className="p-3 rounded-xl border border-white/10 bg-white/[0.03] flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {(c.first_name || c.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            {(c.first_name || c.last_name) && (
                              <p className="font-semibold text-white text-sm truncate">
                                {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                              </p>
                            )}
                            {c.position && <p className="text-gray-400 text-xs truncate">{c.position}</p>}
                            <a href={`mailto:${c.email}`} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mt-1">
                              <FiMail className="w-3 h-3 flex-shrink-0" /> {c.email}
                            </a>
                            {c.phone && (
                              <a href={`tel:${c.phone}`} className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1 mt-1">
                                <FiPhone className="w-3 h-3 flex-shrink-0" /> {c.phone}
                              </a>
                            )}
                            <div className="flex items-center gap-3 mt-1">
                              {c.linkedin && (
                                <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1">
                                  <FiLinkedin className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-gray-400 text-xs mt-2">Powered by Hunter.io</p>
                  </div>
                )}

                {!contacts && !contactsLoading && !contactsError && (
                  <p className="text-gray-500 text-sm">Click "Find Contacts" to look up verified email addresses for this company.</p>
                )}
              </div>
            )}

            {/* SOURCE URLs (live results) */}
            {startup.source_urls && startup.source_urls.length > 0 && (
              <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Sources</p>
                <div className="flex flex-wrap gap-2">
                  {startup.source_urls.map((url, i) => {
                    let domain = url;
                    try { domain = new URL(url).hostname.replace("www.", ""); } catch {}
                    return (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:text-blue-300 underline">{domain}</a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* RAG CHATBOT */}
            {startup.website && (
              <div ref={chatSectionRef} className="rounded-2xl overflow-hidden" style={{
                background: "linear-gradient(135deg, rgba(255,84,31,0.08) 0%, rgba(255,60,0,0.04) 100%)",
                border: "1px solid rgba(255,84,31,0.25)",
                boxShadow: "0 0 40px rgba(255,84,31,0.08)",
              }}>
                {/* Header */}
                <div className="px-6 pt-6 pb-4" style={{ borderBottom: "1px solid rgba(255,84,31,0.12)" }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
                        background: "linear-gradient(135deg, #FF541F, #FF3C00)",
                        boxShadow: "0 4px 12px rgba(255,84,31,0.4)",
                      }}>
                        <span style={{ fontSize: 18 }}>💬</span>
                      </div>
                      <div>
                        <h2 className="font-bold text-white" style={{ fontFamily: "Sora", fontSize: 16 }}>
                          Ask for more insights about {startup.name}
                        </h2>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {chatMessages.length > 0 && (
                        <button
                          onClick={() => setChatMessages([])}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 20,
                            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
                            color: "#9CA3AF", cursor: "pointer",
                          }}
                          onMouseOver={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.1)"; (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
                          onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.05)"; (e.currentTarget as HTMLButtonElement).style.color = "#9CA3AF"; }}
                        >
                          Clear
                        </button>
                      )}
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 20,
                        background: chatReady ? "rgba(34,197,94,0.12)" : "rgba(255,165,0,0.12)",
                        color: chatReady ? "#22C55E" : "#FFA500",
                        border: `1px solid ${chatReady ? "rgba(34,197,94,0.25)" : "rgba(255,165,0,0.25)"}`,
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: "50%",
                          background: chatReady ? "#22C55E" : "#FFA500",
                          display: "inline-block",
                          animation: !chatReady ? "pulse 1.5s infinite" : undefined,
                        }} />
                        {chatReady ? "Ready" : "Indexing..."}
                      </span>
                    </div>
                  </div>

                  {/* Suggested prompts — only shown before first message */}
                  {chatMessages.length === 0 && !chatLoading && (
                    <div className="mt-4">
                      <p className="text-xs mb-2" style={{ color: "#6B7280" }}>✦ Suggested questions</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          `What does ${startup.name} do?`,
                          "Who are the founders?",
                          "What tech stack do they use?",
                          "Who are their customers?",
                          "What are their key products?",
                        ].map((q) => (
                          <button
                            key={q}
                            onClick={() => { setChatInput(q); }}
                            className="text-xs px-3 py-1.5 rounded-full transition-all"
                            style={{
                              background: "rgba(255,84,31,0.08)",
                              border: "1px solid rgba(255,84,31,0.2)",
                              color: "#FB923C",
                              cursor: "pointer",
                              fontFamily: "inherit",
                            }}
                            onMouseOver={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,84,31,0.18)"; }}
                            onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,84,31,0.08)"; }}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Messages area */}
                <div style={{ minHeight: 100, maxHeight: 340, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, padding: "16px 20px", scrollBehavior: "smooth" }}>
                  {chatMessages.length === 0 && !chatLoading && (
                    <div className="flex flex-col items-center justify-center py-6 gap-2" style={{ color: "#4B5563" }}>
                      <span style={{ fontSize: 32 }}>🔍</span>
                      <p style={{ fontSize: 13, textAlign: "center" }}>
                        {chatReady
                          ? `I've analyzed ${startup.name}'s website. Pick a prompt above or type your question.`
                          : "Website is being indexed. You can ask now — first response may take ~20s."}
                      </p>
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                      <div style={{
                        maxWidth: "82%", padding: "10px 14px",
                        borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                        background: msg.role === "user"
                          ? "linear-gradient(135deg, #FF541F, #FF3C00)"
                          : "rgba(255,255,255,0.06)",
                        color: msg.role === "user" ? "#fff" : "#E5E7EB",
                        fontSize: 14, lineHeight: 1.65,
                        border: msg.role === "assistant" ? "1px solid rgba(255,255,255,0.08)" : "none",
                        boxShadow: msg.role === "user" ? "0 4px 12px rgba(255,84,31,0.3)" : "none",
                      }}>
                        {msg.role === "assistant" ? (
                          <ReactMarkdown
                            components={{
                              h1: ({children}) => <p className="text-white font-bold text-sm mb-2">{children}</p>,
                              h2: ({children}) => <p className="text-white font-semibold text-sm mb-1.5 mt-3">{children}</p>,
                              h3: ({children}) => <p className="text-gray-200 font-semibold text-sm mb-1 mt-2">{children}</p>,
                              p: ({children}) => <p className="text-gray-200 text-sm leading-relaxed mb-2 last:mb-0">{children}</p>,
                              strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                              em: ({children}) => <em className="text-gray-300 italic">{children}</em>,
                              ul: ({children}) => <ul className="list-disc list-inside space-y-1 mb-2 text-sm text-gray-200">{children}</ul>,
                              ol: ({children}) => <ol className="list-decimal list-inside space-y-1 mb-2 text-sm text-gray-200">{children}</ol>,
                              li: ({children}) => <li className="text-gray-200 text-sm leading-relaxed">{children}</li>,
                              hr: () => <hr className="border-white/10 my-3" />,
                              code: ({children}) => <code className="bg-white/10 px-1.5 py-0.5 rounded text-xs text-orange-300 font-mono">{children}</code>,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ padding: "10px 14px", borderRadius: "18px 18px 18px 4px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <span style={{ color: "#9CA3AF", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ display: "flex", gap: 3 }}>
                            {[0,1,2].map(i => (
                              <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#FF541F", display: "inline-block", animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
                            ))}
                          </span>
                          {chatIndexing ? "Indexing website..." : "Thinking..."}
                        </span>
                      </div>
                    </div>
                  )}
                  <div ref={chatBottomRef} />
                </div>

                {/* Input bar */}
                <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,84,31,0.12)", display: "flex", gap: 8 }}>
                  <input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                    placeholder={`Ask anything about ${startup.name}...`}
                    disabled={chatLoading}
                    style={{
                      flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 14, padding: "11px 16px", color: "#F9FAFB", fontSize: 14, outline: "none",
                      opacity: chatLoading ? 0.6 : 1, fontFamily: "inherit",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={e => (e.target.style.borderColor = "rgba(255,84,31,0.5)")}
                    onBlur={e => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                  />
                  <button
                    onClick={sendChatMessage}
                    disabled={chatLoading || !chatInput.trim()}
                    style={{
                      padding: "11px 22px", borderRadius: 14, border: "none", cursor: chatLoading || !chatInput.trim() ? "not-allowed" : "pointer",
                      background: chatLoading || !chatInput.trim() ? "rgba(255,84,31,0.2)" : "linear-gradient(135deg, #FF541F, #FF3C00)",
                      color: "#fff", fontWeight: 700, fontSize: 14, transition: "all 0.2s",
                      boxShadow: chatLoading || !chatInput.trim() ? "none" : "0 4px 14px rgba(255,84,31,0.4)",
                    }}
                  >
                    Send ↑
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
      <Footer />
    </div>
  );
};

export default StartupDetailPage;
